/*
  BulkProgramImport — Bulk-import Google Drive spreadsheet programs
  into the "Unassigned" bucket.

  Flow:
    1. Pick a Drive folder (uses existing GDriveFolderPickerDialog).
    2. Backend lists .docx/.xlsx/Google-Sheets files → the user ticks
       the subset to import.
    3. Each file lands as an "Unassigned" program with the parsed
       client name in `pending_assignment`.
    4. Review pane on the right shows one unassigned program at a
       time with an [Assign to client] client-search picker or a
       [Drop] button.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import useStickyState from "@/hooks/useStickyState";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  FolderPlus, Loader2, RefreshCw, Cloud, User, ExternalLink, FileSpreadsheet,
  Trash2, Search, AlertTriangle, ChevronRight, X, Copy, ShieldCheck, RotateCw,
  UserPlus, CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import GDriveFolderPickerDialog from "@/components/GDriveFolderPickerDialog";
import ImportPauseBanner from "@/components/ImportPauseBanner";


function StatusBadge({ text, tone = "amber" }) {
  const map = {
    amber: "bg-amber-50 text-amber-800 border-amber-300",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-300",
    slate: "bg-slate-50 text-slate-700 border-slate-300",
  };
  return <Badge className={cn("text-2xs uppercase tracking-wide", map[tone] || map.amber)}>{text}</Badge>;
}


export default function BulkProgramImport() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedFolder, setPickedFolder] = useState(null); // {id, name}
  const [driveFiles, setDriveFiles] = useState([]);
  // Client-side filter over the loaded folder listing — the Drive
  // listing endpoint already returns every file in a single response,
  // so filtering in-memory keeps interaction instant even for
  // folders with hundreds of files.
  const [driveFilesSearch, setDriveFilesSearch] = useState("");
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const [unassigned, setUnassigned] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(true);
  const [activeProgram, setActiveProgram] = useState(null);
  // Free-text search over filename / parsed client name — hundreds of
  // unassigned rows are otherwise infeasible to eyeball for a
  // specific person. Filtered server-side so pagination stays
  // consistent with the matches (client-side filtering would only
  // narrow the current page).
  const [unassignedSearch, setUnassignedSearch] = useStickyState("", "bulkPrograms.unassignedSearch");
  // Ref mirror seeded from the sticky value so the very first
  // /unassigned fetch already carries the restored search term.
  // Without this, an operator returning to the page after typing
  // "Jones" would see the full list flicker in before the
  // debounced effect fired a second fetch.
  const unassignedSearchRef = useRef(unassignedSearch);
  // Sort mode for the unassigned list — every row shares the same
  // workflow status ("imported/unassigned"), so "status order" is
  // effectively an attention-priority sort: rows with parser
  // quality issues rise to the top, then newest-first. Persisted
  // to localStorage so operators don't reset their preferred view
  // on every visit.
  const [unassignedSort, setUnassignedSort] = useStickyState("newest", "bulkPrograms.unassignedSort");
  // Pagination — render 50 rows at a time so backlogs of hundreds
  // of unassigned programs don't murder React's reconciler on every
  // refresh. Backend supports arbitrary limit/offset via query.
  const UNASSIGNED_PAGE_SIZE = 50;
  const [unassignedPage, setUnassignedPage] = useState(0);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  // Ref-mirror of the current page so a no-arg `loadUnassigned()`
  // call (e.g. Refresh button, pause-banner onChanged) reloads the
  // page the user is actually viewing instead of jumping to page 0.
  const unassignedPageRef = useRef(0);
  // Live progress + per-file error log so a mid-batch crash never leaves
  // the user with a blank screen and no idea what went wrong.
  const [importProgress, setImportProgress] = useState(null); // {done,total,succeeded,failed,cancelled}
  const [importErrors, setImportErrors] = useState([]); // [{file_id, filename, stage, error, error_detail}]
  // Cancel flag — a ref so it survives re-renders inside the async loop.
  const cancelRequested = useRef(false);
  // File ids that were skipped by a cancel — offered up for one-tap resume.
  const [cancelledIds, setCancelledIds] = useState([]);

  const loadUnassigned = useCallback(async (page) => {
    const targetPage = typeof page === "number" ? page : unassignedPageRef.current;
    setLoadingUnassigned(true);
    try {
      const params = {
        limit: UNASSIGNED_PAGE_SIZE,
        offset: targetPage * UNASSIGNED_PAGE_SIZE,
      };
      const needle = (unassignedSearchRef.current || "").trim();
      if (needle) params.q = needle;
      const r = await api.get("/exercise-programs/bulk/unassigned", { params });
      const rows = r.data?.programs || [];
      const total = r.data?.total ?? rows.length;
      // If the current page has emptied (last item on the page was
      // just assigned / dropped) but earlier pages still have rows,
      // slide back one page automatically.
      if (rows.length === 0 && targetPage > 0 && total > 0) {
        const fallback = Math.max(0, Math.min(targetPage - 1, Math.ceil(total / UNASSIGNED_PAGE_SIZE) - 1));
        setLoadingUnassigned(false);
        return loadUnassigned(fallback);
      }
      setUnassigned(rows);
      setUnassignedTotal(total);
      setUnassignedPage(targetPage);
      unassignedPageRef.current = targetPage;
    } catch (e) {
      toast.error("Failed to load unassigned programs", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoadingUnassigned(false); }
  }, []);

  useEffect(() => { loadUnassigned(0); }, [loadUnassigned]);

  // Debounce search — 300ms after the last keystroke, refetch page 0
  // with the new needle. Skips the very first render (already covered
  // by the initial `loadUnassigned(0)` effect above).
  const firstSearchRun = useRef(true);
  useEffect(() => {
    unassignedSearchRef.current = unassignedSearch;
    if (firstSearchRun.current) {
      firstSearchRun.current = false;
      return;
    }
    const t = setTimeout(() => { loadUnassigned(0); }, 300);
    return () => clearTimeout(t);
  }, [unassignedSearch, loadUnassigned]);

  async function loadFilesForFolder(folder) {
    setPickedFolder(folder);
    setLoadingFiles(true);
    setSelectedIds(new Set());
    try {
      const r = await api.get("/exercise-programs/gdrive/files", {
        params: { folder_id: folder.id, include_spreadsheets: true },
      });
      setDriveFiles(r.data?.files || []);
    } catch (e) {
      toast.error("Couldn't list folder files", {
        description: e?.response?.data?.detail || e.message,
      });
      setDriveFiles([]);
    } finally { setLoadingFiles(false); }
  }

  function toggleFile(fileId) {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(fileId)) n.delete(fileId); else n.add(fileId);
      return n;
    });
  }

  function toggleAll() {
    if (selectedIds.size === driveFiles.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(driveFiles.map((f) => f.id)));
  }

  async function runImport(resumeIds = null) {
    // `resumeIds` (optional): a specific list of drive file ids to
    // process instead of the current selection. Used by the Resume
    // button so a cancelled batch can pick up exactly where it left off.
    const ids = resumeIds && resumeIds.length
      ? resumeIds
      : Array.from(selectedIds);
    if (ids.length === 0) return;
    // Global kill switch — refuse to start a new import if imports
    // are paused, so the emergency-stop banner is honoured everywhere.
    try {
      const ps = await api.get("/document-imports/pause-state");
      if (ps.data?.paused) {
        toast.error("Imports are paused globally. Tap Resume above first.");
        return;
      }
    } catch { /* non-fatal — proceed */ }
    cancelRequested.current = false;
    setCancelledIds([]);   // fresh run wipes any prior skip-list
    setImporting(true);
    setImportErrors([]);
    const total = ids.length;
    // Chunk into batches of 10 so we never hit the 60s gateway timeout
    // and the user sees progress after each batch. Each batch survives
    // its own errors — a bad file mid-way doesn't take the rest down.
    const CHUNK = 10;
    let done = 0, succeeded = 0, failed = 0, cancelled = 0;
    const filenameById = Object.fromEntries(driveFiles.map((f) => [f.id, f.name]));
    setImportProgress({ done, total, succeeded, failed, cancelled });
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        if (cancelRequested.current) {
          // Everything still to come is `cancelled`. Capture the ids
          // so the user can Resume.
          const skipped = ids.slice(i);
          cancelled = skipped.length;
          setCancelledIds(skipped);
          break;
        }
        const batch = ids.slice(i, i + CHUNK);
        try {
          const r = await api.post(
            "/exercise-programs/gdrive/bulk-unassigned-import",
            { file_ids: batch },
            { timeout: 180000 },  // 3-min per-batch ceiling
          );
          const results = r.data?.results || [];
          succeeded += r.data?.imported || 0;
          failed += r.data?.failed || 0;
          const errs = results.filter((x) => !x.ok).map((x) => ({
            file_id: x.file_id,
            filename: filenameById[x.file_id] || x.file_id,
            stage: x.stage,
            error: x.error,
            error_detail: x.error_detail,
            error_traceback: x.error_traceback,
          }));
          if (errs.length) setImportErrors((prev) => [...prev, ...errs]);
        } catch (e) {
          // Batch-level failure (network, timeout, 5xx): mark every
          // file in this batch as failed so the count is honest.
          failed += batch.length;
          const detail = e?.response?.data?.detail || e.message || "batch failed";
          const errs = batch.map((id) => ({
            file_id: id,
            filename: filenameById[id] || id,
            stage: "batch_request",
            error: e?.response?.status ? `HTTP ${e.response.status}` : "NetworkError",
            error_detail: typeof detail === "string" ? detail : JSON.stringify(detail),
          }));
          setImportErrors((prev) => [...prev, ...errs]);
        }
        done += batch.length;
        setImportProgress({ done, total, succeeded, failed, cancelled });
      }
      if (cancelRequested.current) {
        toast.warning(
          `Cancelled — ${succeeded} imported, ${cancelled} skipped`,
          {
            description: (failed
              ? `${failed} failed. `
              : "")
              + `Tap “Resume the ones I cancelled” to pick up where you left off.`,
          },
        );
      } else {
        toast.success(
          `Imported ${succeeded} of ${total} program(s)`,
          failed ? { description: `${failed} failed — see the Error log below.` } : undefined,
        );
      }
      // On a full-through run, wipe the selection; on a cancelled run
      // KEEP the selection so the user can Resume without re-picking.
      if (!cancelRequested.current) setSelectedIds(new Set());
      // After a successful import batch, jump to page 0 — the newest
      // programs land at the top of the list and the user almost
      // always wants to see them first.
      await loadUnassigned(0);
    } catch (e) {
      // Belt-and-braces catch — should never fire because inner
      // try/catches handle everything, but if it does we still show
      // a toast instead of blank-screening.
      toast.error("Import crashed unexpectedly", {
        description: e?.message || String(e),
      });
      setImportErrors((prev) => [...prev, {
        file_id: "-", filename: "(top-level)", stage: "runImport",
        error: e?.name || "Error", error_detail: e?.message || String(e),
      }]);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="p-6 space-y-4" data-testid="bulk-program-import-page">
      <div className="flex items-center gap-3">
        <FileSpreadsheet className="h-6 w-6 text-indigo-700" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold">Bulk Import Programs</h1>
          <div className="text-sm text-zinc-500">
            Import .docx / .xlsx / Google Sheets programs from Drive.
            Client info is read from the file itself — you assign each
            program to a real client after review.
          </div>
        </div>
        <Button
          onClick={() => setPickerOpen(true)}
          data-testid="pick-drive-folder-btn"
        >
          <FolderPlus className="h-4 w-4 mr-1" /> Pick Drive folder
        </Button>
      </div>

      <ImportPauseBanner onChanged={loadUnassigned} />

      <RetrofitPrecautionsPanel />

      {/* Folder + file selection */}
      {pickedFolder && (
        <Card className="p-3 rounded-lg border border-zinc-200 shadow-none"
              data-testid="bulk-folder-files-panel">
          <div className="flex items-center gap-2 mb-3">
            <Cloud className="h-4 w-4 text-indigo-700" />
            <div className="text-sm font-medium">{pickedFolder.name}</div>
            <div className="text-xs text-zinc-500">
              {loadingFiles ? "Loading…" : `${driveFiles.length} file(s)`}
            </div>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => loadFilesForFolder(pickedFolder)}
                    disabled={loadingFiles}
                    data-testid="reload-folder-files">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
            </Button>
            <Button size="sm" variant="ghost" onClick={toggleAll} disabled={driveFiles.length === 0}
                    data-testid="toggle-all-files">
              {selectedIds.size === driveFiles.length ? "Deselect all" : "Select all"}
            </Button>
            <Button size="sm" onClick={runImport}
                    disabled={selectedIds.size === 0 || importing}
                    data-testid="run-bulk-import-btn">
              {importing
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Importing…</>
                : <>Import {selectedIds.size || ""} file(s)</>}
            </Button>
            {importing && (
              <Button
                size="sm" variant="outline"
                onClick={() => { cancelRequested.current = true; }}
                className="text-red-700 border-red-300 hover:bg-red-50"
                data-testid="cancel-bulk-import-btn"
                title="Stop importing after the current batch"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            )}
          </div>
          {loadingFiles ? (
            <div className="text-sm text-zinc-500 py-4">Loading files…</div>
          ) : driveFiles.length === 0 ? (
            <div className="text-sm text-zinc-500 py-4">
              No .docx, .xlsx or Google Sheets files directly inside this folder.
            </div>
          ) : (
            <>
              {/* Filter the loaded listing by filename — the list is
                  already fully in-memory, so a plain client-side
                  filter is enough. */}
              <div className="relative mb-2 max-w-sm">
                <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={driveFilesSearch}
                  onChange={(e) => setDriveFilesSearch(e.target.value)}
                  placeholder="Search files…"
                  className="h-8 pl-7 pr-7 text-xs"
                  data-testid="drive-files-search-input"
                />
                {driveFilesSearch && (
                  <button
                    type="button"
                    onClick={() => setDriveFilesSearch("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100"
                    aria-label="Clear search"
                    data-testid="drive-files-search-clear"
                  >
                    <X className="h-3 w-3 text-zinc-500" />
                  </button>
                )}
              </div>
              {(() => {
                const needle = driveFilesSearch.trim().toLowerCase();
                const filtered = needle
                  ? driveFiles.filter((f) => (f.name || "").toLowerCase().includes(needle))
                  : driveFiles;
                if (filtered.length === 0) {
                  return (
                    <div className="text-sm text-zinc-500 py-4 text-center"
                          data-testid="drive-files-empty">
                      No files match “<b>{driveFilesSearch.trim()}</b>”.
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto"
                        data-testid="drive-files-list">
                    {filtered.map((f) => (
                      <label key={f.id}
                             className={cn(
                               "flex items-center gap-2 p-2 rounded border cursor-pointer",
                               selectedIds.has(f.id) ? "border-indigo-300 bg-indigo-50/40" : "border-zinc-200",
                             )}
                             data-testid={`drive-file-${f.id}`}>
                        <input type="checkbox" checked={selectedIds.has(f.id)}
                               onChange={() => toggleFile(f.id)}
                               className="h-4 w-4"
                               data-testid={`drive-file-checkbox-${f.id}`} />
                        <FileSpreadsheet className="h-4 w-4 text-zinc-500" />
                        <div className="text-sm truncate flex-1">{f.name}</div>
                        <Badge variant="outline" className="text-2xs">
                          {(f.mimeType || "").includes("spreadsheet")
                            ? (f.mimeType.includes("google-apps") ? "Sheet" : "xlsx")
                            : (f.mimeType || "").includes("wordprocessing") ? "docx" : "?"}
                        </Badge>
                      </label>
                    ))}
                  </div>
                );
              })()}
            </>
          )}

          {/* Live progress bar during import */}
          {importProgress && (
            <div className="mt-3 pt-3 border-t border-zinc-200"
                 data-testid="import-progress">
              <div className="flex items-center justify-between text-xs text-zinc-700 mb-1">
                <div className="inline-flex items-center gap-1">
                  {importing && <Loader2 className="h-3 w-3 animate-spin text-indigo-700" />}
                  {importing ? "Importing…" : "Import finished"} {importProgress.done}/{importProgress.total}
                </div>
                <div className="text-zinc-500 flex items-center gap-2 flex-wrap justify-end">
                  <span className="text-emerald-700">✓ {importProgress.succeeded}</span>
                  {importProgress.failed > 0 && (
                    <span className="text-red-700">✗ {importProgress.failed}</span>
                  )}
                  {importProgress.cancelled > 0 && (
                    <span className="text-amber-700">⊘ {importProgress.cancelled} skipped</span>
                  )}
                  {!importing && cancelledIds.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => runImport(cancelledIds)}
                      className="h-6 text-2xs bg-indigo-600 hover:bg-indigo-700 ml-1"
                      data-testid="resume-bulk-import-btn"
                      title="Import the files you skipped when you cancelled"
                    >
                      <RotateCw className="h-3 w-3 mr-1" />
                      Resume the ones I cancelled ({cancelledIds.length})
                    </Button>
                  )}
                  {!importing && importErrors.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => runImport(importErrors.map((e) => e.file_id))}
                      className="h-6 text-2xs bg-amber-600 hover:bg-amber-700 ml-1"
                      data-testid="retry-failed-bulk-import-btn"
                      title="Re-import just the files that failed above"
                    >
                      <RotateCw className="h-3 w-3 mr-1" />
                      Retry only the failed files ({importErrors.length})
                    </Button>
                  )}
                </div>
              </div>
              <div className="h-2 bg-zinc-100 rounded overflow-hidden">
                <div
                  className={cn(
                    "h-full transition-all",
                    importProgress.cancelled > 0 ? "bg-amber-500" : "bg-indigo-600",
                  )}
                  style={{
                    width: `${importProgress.total > 0
                      ? Math.min(100, Math.round((importProgress.done / importProgress.total) * 100))
                      : 0}%`,
                  }}
                  data-testid="import-progress-bar"
                />
              </div>
            </div>
          )}

          {/* Per-file error log — visible even if the whole batch times out */}
          {importErrors.length > 0 && (
            <div className="mt-3 pt-3 border-t border-red-200"
                 data-testid="import-errors-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wide text-red-700 inline-flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {importErrors.length} error{importErrors.length === 1 ? "" : "s"}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost"
                          onClick={() => navigator.clipboard?.writeText(JSON.stringify(importErrors, null, 2))}
                          data-testid="copy-errors-btn">
                    <Copy className="h-3 w-3 mr-1" /> Copy JSON
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setImportErrors([])}
                          data-testid="clear-errors-btn">
                    <X className="h-3 w-3 mr-1" /> Clear
                  </Button>
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto border border-red-200 rounded bg-red-50/60">
                <table className="w-full text-2xs font-mono">
                  <thead className="bg-red-100 text-red-800 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">File</th>
                      <th className="text-left px-2 py-1">Stage</th>
                      <th className="text-left px-2 py-1">Error</th>
                      <th className="text-left px-2 py-1">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importErrors.map((e, i) => (
                      <tr key={i} className="border-t border-red-200 align-top"
                          data-testid={`import-error-row-${i}`}>
                        <td className="px-2 py-1 max-w-[180px] truncate" title={e.filename}>{e.filename}</td>
                        <td className="px-2 py-1 text-red-800">{e.stage || "-"}</td>
                        <td className="px-2 py-1 text-red-800">{e.error || "-"}</td>
                        <td className="px-2 py-1 whitespace-pre-wrap break-all text-zinc-700">{e.error_detail || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Unassigned review pane */}
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-4 p-3 rounded-lg border border-zinc-200 shadow-none max-h-[75vh] overflow-y-auto"
              data-testid="unassigned-list-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-zinc-500">
              Unassigned ({unassignedTotal})
            </div>
            <div className="flex items-center gap-1">
              {/*
                Sort mode — "priority" surfaces rows the parser
                flagged with quality issues (missing dose, unknown
                exercise, low-confidence match) so the operator
                clears the tricky ones first. Ties break on
                newest-first.
              */}
              <select
                value={unassignedSort}
                onChange={(e) => setUnassignedSort(e.target.value)}
                className="h-6 text-2xs rounded border border-zinc-300 bg-white px-1 text-zinc-700"
                title="Sort unassigned programs"
                data-testid="unassigned-sort"
              >
                <option value="newest">Newest first</option>
                <option value="priority">Priority (issues first)</option>
              </select>
              <Button size="sm" variant="ghost" onClick={() => loadUnassigned()}
                      disabled={loadingUnassigned}
                      data-testid="refresh-unassigned">
                <RefreshCw className="h-3 w-3 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
          {/* Free-text filter — matches filename or the parsed client
              name so the operator can jump to a specific person in a
              backlog of hundreds. Server-side so pagination stays
              consistent across matches. */}
          <div className="relative mb-2">
            <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={unassignedSearch}
              onChange={(e) => setUnassignedSearch(e.target.value)}
              placeholder="Search filename or client…"
              className="h-8 pl-7 pr-7 text-xs"
              data-testid="unassigned-search-input"
            />
            {unassignedSearch && (
              <button
                type="button"
                onClick={() => setUnassignedSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100"
                aria-label="Clear search"
                data-testid="unassigned-search-clear"
              >
                <X className="h-3 w-3 text-zinc-500" />
              </button>
            )}
          </div>
          {loadingUnassigned ? (
            <div className="text-sm text-zinc-500 py-4">Loading…</div>
          ) : unassigned.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center" data-testid="unassigned-empty">
              {unassignedSearch.trim()
                ? <>No matches for “<b>{unassignedSearch.trim()}</b>”.</>
                : "No unassigned programs. Pick a Drive folder above to start."}
            </div>
          ) : (
            /*
              Priority sort: rows with parser-flagged quality issues
              float to the top so the operator handles the tricky
              ones first. Within the same priority group the API's
              newest-first order is preserved via a stable sort.
            */
            (unassignedSort === "priority"
              ? [...unassigned].sort((a, b) => {
                  const ai = (a.quality_issues?.length || 0) > 0 ? 0 : 1;
                  const bi = (b.quality_issues?.length || 0) > 0 ? 0 : 1;
                  return ai - bi;
                })
              : unassigned
            ).map((p) => {
              const pa = p.pending_assignment || {};
              const who = [pa.client_firstname_from_file, pa.client_surname_from_file]
                .filter(Boolean).join(" ") || "(no name parsed)";
              return (
                <button key={p.id}
                        onClick={() => setActiveProgram(p)}
                        className={cn(
                          "w-full text-left p-2 rounded hover:bg-zinc-100 mb-1 border",
                          activeProgram?.id === p.id
                            ? "border-indigo-300 bg-indigo-50/40"
                            : "border-transparent",
                        )}
                        data-testid={`unassigned-item-${p.id}`}>
                  <div className="text-sm font-medium truncate">{who}</div>
                  <div className="text-2xs text-zinc-500 truncate">
                    {p.source?.filename || p.title}
                  </div>
                  <div className="text-2xs text-zinc-400 flex items-center gap-1 mt-0.5 flex-wrap">
                    {p.source?.source_tab && (
                      <StatusBadge text={p.source.source_tab} tone="slate" />
                    )}
                    {(p.tags || []).map((t) => (
                      <StatusBadge key={t} text={t} tone="emerald" />
                    ))}
                    {p.source?.layout_variant && (
                      <span className="text-zinc-400">· {p.source.layout_variant}</span>
                    )}
                    {(p.quality_issues || []).length > 0 && (
                      <AlertTriangle className="h-3 w-3 text-amber-600" />
                    )}
                    <span>· {p.exercise_count} ex</span>
                  </div>
                </button>
              );
            })
          )}
          {/* Pagination footer — visible whenever the backlog exceeds
              one page. We show 50 at a time to keep React responsive
              even for backlogs of hundreds of unassigned programs. */}
          {unassignedTotal > UNASSIGNED_PAGE_SIZE && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-200 text-2xs text-zinc-600"
                 data-testid="unassigned-pagination">
              <div>
                {unassigned.length === 0
                  ? "0 of " + unassignedTotal
                  : `${unassignedPage * UNASSIGNED_PAGE_SIZE + 1}–${unassignedPage * UNASSIGNED_PAGE_SIZE + unassigned.length} of ${unassignedTotal}`}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline"
                        className="h-6 px-2 text-2xs"
                        onClick={() => loadUnassigned(unassignedPage - 1)}
                        disabled={loadingUnassigned || unassignedPage <= 0}
                        data-testid="unassigned-prev">
                  Prev
                </Button>
                <Button size="sm" variant="outline"
                        className="h-6 px-2 text-2xs"
                        onClick={() => loadUnassigned(unassignedPage + 1)}
                        disabled={loadingUnassigned || (unassignedPage + 1) * UNASSIGNED_PAGE_SIZE >= unassignedTotal}
                        data-testid="unassigned-next">
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        <div className="col-span-8">
          {!activeProgram ? (
            <Card className="p-8 rounded-lg border border-dashed border-zinc-300 shadow-none text-center text-sm text-zinc-500">
              Pick an unassigned program on the left to review and assign it to a client.
            </Card>
          ) : (
            <UnassignedReviewPane
              program={activeProgram}
              onChanged={async () => {
                await loadUnassigned();
                setActiveProgram(null);
              }}
            />
          )}
        </div>
      </div>

      <GDriveFolderPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={async (folder) => {
          setPickerOpen(false);
          await loadFilesForFolder(folder);
        }}
      />
    </div>
  );
}


function UnassignedReviewPane({ program, onChanged }) {
  const pa = program.pending_assignment || {};
  const who = [pa.client_firstname_from_file, pa.client_surname_from_file]
    .filter(Boolean).join(" ") || "";
  const [search, setSearch] = useState(who);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [triedSearch, setTriedSearch] = useState(false);
  const [suggestion, setSuggestion] = useState(null);      // top match
  const [suggestionAccepted, setSuggestionAccepted] = useState(false);

  useEffect(() => {
    let cancel = false;
    async function loadDetail() {
      setLoadingDetail(true);
      try {
        // Fetch the full program (sections + exercises) for preview.
        const r = await api.get(`/exercise-programs/${program.id}`);
        if (!cancel) setDetail(r.data);
      } catch { /* ignore */ }
      finally { if (!cancel) setLoadingDetail(false); }
    }
    async function loadSuggestion() {
      try {
        const r = await api.get(
          `/exercise-programs/bulk/unassigned/${program.id}/suggestions`);
        if (!cancel) {
          const top = (r.data?.candidates || [])[0] || null;
          setSuggestion(top);
        }
      } catch { /* silent — suggestion is a nicety */ }
    }
    loadDetail();
    loadSuggestion();
    setSuggestionAccepted(false);
    return () => { cancel = true; };
  }, [program.id]);

  async function runSearch() {
    if (search.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    setTriedSearch(true);
    try {
      const r = await api.post("/exercise-programs/bulk/search-clients",
                                { q: search.trim() });
      setResults(r.data?.results || []);
    } catch (e) {
      toast.error("Search failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSearching(false); }
  }

  async function assign(email) {
    setAssigning(true);
    try {
      await api.post(`/exercise-programs/bulk/unassigned/${program.id}/assign`,
                      { client_email: email });
      toast.success(`Assigned to ${email}`);
      await onChanged();
    } catch (e) {
      toast.error("Assign failed", { description: e?.response?.data?.detail || e.message });
    } finally { setAssigning(false); }
  }

  async function drop() {
    if (!window.confirm("Drop this unassigned program? This soft-deletes it.")) return;
    setDropping(true);
    try {
      await api.delete(`/exercise-programs/bulk/unassigned/${program.id}`);
      toast.success("Dropped");
      await onChanged();
    } catch (e) {
      toast.error("Drop failed", { description: e?.response?.data?.detail || e.message });
    } finally { setDropping(false); }
  }

  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-4"
          data-testid="unassigned-review-pane">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm text-zinc-500">Parsed from file</div>
          <div className="text-lg font-medium">{who || "(no name parsed)"}</div>
          <div className="text-xs text-zinc-500 truncate">
            {program.source?.filename}
            {program.source?.source_tab && <> · latest tab: <span className="font-medium">{program.source.source_tab}</span></>}
            {program.source?.layout_variant && <> · {program.source.layout_variant}</>}
          </div>
          {(program.tags || []).length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-1"
                 data-testid="unassigned-tags">
              <span className="text-2xs text-zinc-500">Tags:</span>
              {(program.tags || []).map((t) => (
                <Badge key={t}
                       className="text-2xs bg-emerald-50 text-emerald-800 border-emerald-300">
                  {t}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {program.source?.gdrive_link && (
          <a href={program.source.gdrive_link} target="_blank" rel="noreferrer"
             className="text-2xs text-indigo-700 inline-flex items-center gap-1 hover:underline">
            <ExternalLink className="h-3 w-3" /> Open in Drive
          </a>
        )}
      </div>

      {(program.quality_issues || []).length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded p-2 text-xs text-amber-800">
          <div className="font-semibold flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Quality notes
          </div>
          <ul className="list-disc list-inside">
            {program.quality_issues.map((qi, i) => <li key={i}>{qi}</li>)}
          </ul>
        </div>
      )}

      {/* Assign to client */}
      <div className="border border-zinc-200 rounded p-3 space-y-2">
        <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
          <User className="h-3 w-3" /> Assign to client
        </div>

        {/* Suggested match banner — top-scored candidate from the
            /suggestions endpoint. One-click "Accept & attach" runs
            the same assign flow the manual picker uses. Manual
            search stays visible below so the reviewer can ignore
            this banner if the guess is wrong. */}
        {suggestion && !suggestionAccepted && (
          <div className="flex items-center gap-3 rounded-md border border-emerald-300
                           bg-emerald-50 px-3 py-2"
               data-testid="bulk-suggested-match-banner">
            <div className="flex-1 min-w-0">
              <div className="text-2xs uppercase tracking-wide text-emerald-800
                               font-semibold flex items-center gap-1">
                Suggested match
                <span className="ml-1 text-2xs px-1 rounded border border-emerald-400 bg-white text-emerald-800">
                  {suggestion.confidence}
                </span>
                {suggestion.source && (
                  <span className="ml-1 text-2xs px-1 rounded border border-zinc-300 bg-white text-zinc-600">
                    {suggestion.source}
                  </span>
                )}
              </div>
              <div className="text-sm text-emerald-900 truncate">
                <b>{suggestion.client_name || suggestion.client_email}</b>
                <span className="text-emerald-700"> · {suggestion.client_email}</span>
              </div>
            </div>
            <Button
              size="sm"
              disabled={assigning}
              onClick={async () => {
                setSuggestionAccepted(true);
                await assign(suggestion.client_email);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
              data-testid="accept-bulk-suggested-match"
            >
              {assigning
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Attaching</>
                : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept & attach</>}
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or phone"
            data-testid="assign-client-search"
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
          />
          <Button size="sm" variant="secondary" onClick={runSearch}
                  disabled={searching}
                  data-testid="assign-client-search-btn">
            {searching
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Searching</>
              : <><Search className="h-3.5 w-3.5 mr-1" /> Search</>}
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setCreateOpen(true)}
                  data-testid="assign-open-create-client-btn"
                  title="Create a new client if they don't exist yet">
            <UserPlus className="h-3.5 w-3.5 mr-1" /> New client
          </Button>
        </div>
        {results.length > 0 && (
          <div className="border border-zinc-200 rounded max-h-56 overflow-y-auto">
            {results.map((r) => (
              <button key={r.client_email}
                      onClick={() => assign(r.client_email)}
                      disabled={assigning}
                      className="w-full text-left px-3 py-2 border-b last:border-b-0 border-zinc-100 hover:bg-indigo-50/40 flex items-center gap-2"
                      data-testid={`assign-result-${r.client_email}`}>
                <User className="h-3.5 w-3.5 text-zinc-500" />
                <div className="flex-1">
                  <div className="text-sm">{r.client_name || r.client_email}</div>
                  <div className="text-2xs text-zinc-500">{r.client_email}{r.client_phone ? ` · ${r.client_phone}` : ""}</div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
              </button>
            ))}
          </div>
        )}
        {triedSearch && !searching && results.length === 0 && search.trim().length >= 2 && (
          <div className="border border-dashed border-amber-300 bg-amber-50/40 rounded p-2 flex items-center justify-between gap-2"
               data-testid="assign-no-results-cta">
            <div className="text-xs text-amber-900">
              No client matches “<b>{search.trim()}</b>”. Create a new one?
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    className="h-7 text-2xs"
                    data-testid="assign-create-from-empty-btn">
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Create client
            </Button>
          </div>
        )}
      </div>

      {/* Program preview */}
      <div className="border border-zinc-200 rounded p-3 max-h-96 overflow-y-auto">
        <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Program preview</div>
        {loadingDetail ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : !detail ? (
          <div className="text-sm text-zinc-500">Could not load preview.</div>
        ) : (
          <div className="space-y-3">
            {(detail.sections || []).map((s, i) => (
              <div key={i}>
                <div className="text-sm font-medium">{s.name}</div>
                <ul className="text-xs text-zinc-700 list-disc list-inside space-y-0.5">
                  {(s.exercises || []).map((e, j) => (
                    <li key={j}>
                      <span className="font-medium">{e.name}</span>
                      {e.dosage && <span className="text-zinc-500"> · {e.dosage}</span>}
                      {e.notes && <span className="text-zinc-400"> ({e.notes})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={drop} disabled={dropping}
                className="text-red-700 hover:text-red-800"
                data-testid="drop-unassigned-btn">
          <Trash2 className="h-4 w-4 mr-1" /> Drop
        </Button>
      </div>

      <CreateClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        prefillFirst={pa.client_firstname_from_file || ""}
        prefillLast={pa.client_surname_from_file || ""}
        onCreated={async (email) => {
          setCreateOpen(false);
          await assign(email);
        }}
      />
    </Card>
  );
}


// ---------------------------------------------------------------------------
// CreateClientDialog — used from the assign flow when the client doesn't
// yet exist. Pre-fills first + last from what the parser pulled off the
// filename. On success, calls onCreated with the resolved email so the
// caller can immediately assign the program.
// ---------------------------------------------------------------------------
function CreateClientDialog({ open, onOpenChange, prefillFirst, prefillLast,
                                onCreated }) {
  const [first, setFirst] = useState(prefillFirst || "");
  const [last, setLast] = useState(prefillLast || "");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFirst(prefillFirst || "");
      setLast(prefillLast || "");
      setEmail("");
      setPhone("");
      setDob("");
    }
  }, [open, prefillFirst, prefillLast]);

  async function save() {
    if (!first.trim() && !last.trim()) {
      toast.error("First or last name is required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/intake/clients/manual", {
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        dob: dob.trim() || null,
      });
      const createdEmail = r.data.client_email;
      if (r.data.was_existing) {
        toast.info(`Client already existed — using ${createdEmail}`);
      } else if (r.data.generated_email) {
        toast.success("Created client (no-email placeholder)", {
          description: `Update their email later once you have it.`,
        });
      } else {
        toast.success(`Created ${createdEmail}`);
      }
      onCreated?.(createdEmail);
    } catch (e) {
      toast.error("Create failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-client-dialog">
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
          <DialogDescription>
            Creates a lightweight client record so this program can be
            assigned. If you leave email blank we generate a placeholder
            you can update later.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <Label className="text-2xs uppercase text-zinc-500">First name</Label>
            <Input value={first} onChange={(e) => setFirst(e.target.value)}
                    data-testid="new-client-first" autoFocus />
          </div>
          <div>
            <Label className="text-2xs uppercase text-zinc-500">Last name</Label>
            <Input value={last} onChange={(e) => setLast(e.target.value)}
                    data-testid="new-client-last" />
          </div>
          <div className="col-span-2">
            <Label className="text-2xs uppercase text-zinc-500">Email (optional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Leave blank if unknown"
                    data-testid="new-client-email" />
          </div>
          <div>
            <Label className="text-2xs uppercase text-zinc-500">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                    data-testid="new-client-phone" />
          </div>
          <div>
            <Label className="text-2xs uppercase text-zinc-500">DOB</Label>
            <Input value={dob} onChange={(e) => setDob(e.target.value)}
                    placeholder="YYYY-MM-DD or year"
                    data-testid="new-client-dob" />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}
                  data-testid="new-client-save">
            {saving
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating…</>
              : <><UserPlus className="h-3.5 w-3.5 mr-1" /> Create & assign</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// RetrofitPrecautionsPanel — one-tap bulk backfill of the worksheet's
// Precautions / pathologies header from each client's active medical
// alerts + history. Dry-run first, then apply. Owner/manager only.
// ---------------------------------------------------------------------------
function RetrofitPrecautionsPanel() {
  const [preview, setPreview] = useState(null); // API result of the dry-run
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [expanded, setExpanded] = useState(false);

  async function runDryRun() {
    setRunning(true);
    try {
      const r = await api.post(
        "/exercise-programs/bulk/retrofit-precautions",
        { dry_run: true, overwrite },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error("Preview failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setRunning(false); }
  }

  async function apply() {
    if (!preview || preview.would_fill === 0) {
      toast.info("Nothing to retrofit — every program is already covered.");
      return;
    }
    if (!window.confirm(
      `Update ${preview.would_fill} program(s) with retrofitted precautions?` +
      (overwrite ? "\n\n⚠ Overwrite is ON — existing text will be REPLACED." : ""),
    )) return;
    setApplying(true);
    try {
      const r = await api.post(
        "/exercise-programs/bulk/retrofit-precautions",
        { dry_run: false, overwrite },
      );
      toast.success(`Retrofitted ${r.data.filled} program(s)`);
      // Re-run the dry-run so badges/sample/Apply-button reflect the
      // post-apply state (nothing left to fill).
      const rp = await api.post(
        "/exercise-programs/bulk/retrofit-precautions",
        { dry_run: true, overwrite },
      );
      setPreview(rp.data);
    } catch (e) {
      toast.error("Retrofit failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setApplying(false); }
  }

  return (
    <Card
      className="p-3 rounded-lg border border-indigo-200 shadow-none bg-indigo-50/40"
      data-testid="retrofit-precautions-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-indigo-700" />
        <div className="font-medium text-sm text-indigo-900">
          Retrofit Precautions / Pathologies
        </div>
        <span className="text-xs text-zinc-600">
          Fill the worksheet header on old programs from each client&apos;s active medical alerts &amp; history.
        </span>
        <div className="flex-1" />
        <label className="flex items-center gap-1 text-2xs text-zinc-700 cursor-pointer">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => { setOverwrite(e.target.checked); setPreview(null); }}
            className="accent-indigo-600"
            data-testid="retrofit-overwrite"
          />
          Overwrite existing text
        </label>
        <Button
          size="sm" variant="outline"
          onClick={runDryRun}
          disabled={running}
          data-testid="retrofit-preview-btn"
        >
          {running
            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Scanning…</>
            : <><Search className="h-3.5 w-3.5 mr-1" /> Preview</>}
        </Button>
        <Button
          size="sm"
          onClick={apply}
          disabled={applying || !preview || preview.would_fill === 0}
          data-testid="retrofit-apply-btn"
        >
          {applying
            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Applying…</>
            : <>Apply{preview ? ` (${preview.would_fill})` : ""}</>}
        </Button>
      </div>

      {preview && (
        <div className="mt-2 text-xs text-zinc-800">
          <div className="flex gap-3 flex-wrap" data-testid="retrofit-stats">
            <StatusBadge tone="slate" text={`Scanned ${preview.scanned}`} />
            <StatusBadge tone="emerald" text={`${preview.would_fill} to fill`} />
            <StatusBadge tone="amber" text={`${preview.skipped_has_value} kept (has text)`} />
            <StatusBadge tone="slate" text={`${preview.skipped_no_data} skipped (no PHR data)`} />
          </div>
          {preview.sample?.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-indigo-700 hover:underline text-2xs"
                data-testid="retrofit-toggle-sample"
              >
                {expanded ? "Hide sample" : `Show sample (${preview.sample.length})`}
              </button>
              {expanded && (
                <div className="mt-1 max-h-56 overflow-y-auto border border-indigo-200 rounded bg-white">
                  <table className="w-full text-2xs">
                    <thead className="bg-indigo-100 text-indigo-800 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Client</th>
                        <th className="text-left px-2 py-1">Program</th>
                        <th className="text-left px-2 py-1">Before</th>
                        <th className="text-left px-2 py-1">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.map((row, i) => (
                        <tr key={i} className="border-t border-indigo-100 align-top"
                            data-testid={`retrofit-sample-row-${i}`}>
                          <td className="px-2 py-1 max-w-[180px] truncate" title={row.client_email}>
                            {row.client_email}
                          </td>
                          <td className="px-2 py-1 max-w-[180px] truncate" title={row.title}>
                            {row.title}
                          </td>
                          <td className="px-2 py-1 text-zinc-500 italic">
                            {row.before || "(empty)"}
                          </td>
                          <td className="px-2 py-1 text-emerald-800 whitespace-pre-wrap">
                            {row.after}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
